/**
 * The session: who is signed in, what they may do, and what this installation has.
 *
 * One store, filled by one call. `/auth/me` returns the identity, the resolved
 * permissions, the enabled services, the navigation manifest and the scope
 * options together — because the shell cannot draw anything until it knows all
 * of it, and fetching them separately means rendering a sidebar and then
 * changing it.
 *
 * **Every check here is UX.** The permissions and services in this store decide
 * what to *show*; the API re-decides everything on every call. A client-side
 * check exists so a user is not offered a door that will not open, never as the
 * lock.
 */

import { create } from 'zustand';

import {
  api,
  setAccessToken,
  setActiveScope,
  setConsentRequiredHandler,
  setUnauthenticatedHandler,
} from '../lib/api-client';
import { removeJSON, saveJSON } from '../lib/persist';
import { EMPTY } from '../lib/empty';
import { servedKind } from '../config/app-config';

export interface NavItem {
  id: string;
  label: string;
  service: string;
  permission: string | null;
  group: string | null;
  order: number;
  badge: string | null;
  /**
   * A feature the selected service must have for the module to show — `chat`,
   * which a WhatsApp service has and an SMS one does not. `null`: any service
   * of the module's own.
   */
  feature?: string | null;
  cross_scope: boolean;
  /**
   * The module's own pages (Messages' Sent, New message…), each a module of
   * the catalog with its own permission and feature — so which of them a
   * service has is the server's to say. Already filtered by permission; the
   * active service's features still decide which are shown.
   */
  pages?: NavPage[];
  /**
   * Where the module's own page sits among `pages`, by their `order` —
   * Messages' Sent just above Reports. `null` or absent: before them all.
   */
  home_order?: number | null;
}

/** One of a module's pages, as the manifest serves it beneath the module. */
export interface NavPage {
  id: string;
  label: string;
  service: string;
  permission: string | null;
  order: number;
  feature?: string | null;
}

export interface NavGroup {
  group: string | null;
  items: NavItem[];
}

export interface ScopeOption {
  kind: string;
  label: string;
  department_public_id: string | null;
  team_public_id: string | null;
  active: boolean;
}

/**
 * One entry in a client's service selector.
 *
 * A client has less than the installation: the portal features an administrator
 * switched on for it, and one messaging entry per live connection it has. The
 * server has already narrowed `services` to these, so this list is what to
 * *label* the choice with — never what decides whether a call is allowed.
 */
export interface ClientService {
  /** Stable across sessions: the feature id, or the connection's public id. */
  id: string;
  kind: 'portal' | 'messaging';
  /** The Service Switch id the entry belongs to. */
  service: 'support' | 'finance' | 'messaging';
  label: string;
  /** For a messaging entry, the channel (`sms`). */
  channel: string | null;
  /** For a messaging entry, the connection every messaging call is addressed to. */
  channel_public_id: string | null;
  /** What the entry can do beyond sending: `chat` for a service holding conversations. */
  features?: string[];
  /**
   * What this service calls a module, by id, where the catalog's word is not
   * what the page is here — an SMS service's "Templates and sender IDs", an
   * email service's "Email campaigns".
   */
  labels?: Record<string, string>;
}

/** A member of a client's team (`app.modules.team`), as their session says. */
export interface SessionMember {
  public_id: string;
  name: string;
  role: 'manager' | 'agent';
  availability: 'available' | 'away';
  chat_scope: 'all' | 'mine';
}

export interface SessionUser {
  /** The staff account's id, or the client's for a portal session. */
  public_id: string;
  /** A staff account's username. `null` for a client's portal session. */
  username: string | null;
  /** A staff account's address, or a client's login email. */
  email: string | null;
  /** A client's login mobile number, or a staff account's sign-in mobile. */
  mobile: string | null;
  /**
   * Whether the account has a password. A client that signs in with codes may
   * have none, and sets its first without being asked for a current one.
   */
  has_password: boolean;
  kind: 'employee' | 'client_user' | 'partner_user';
  full_name: string;
  is_superuser: boolean;
  must_change_password: boolean;
  activation_pending: boolean;
  /**
   * The employee record behind a staff session, when there is one.
   *
   * For answering "is this row mine" — a timesheet to submit, a claim to
   * approve. Never for authority: what somebody may do comes from
   * `permissions`, and the server decides regardless.
   */
  employee_public_id: string | null;
  /**
   * The member of the client's team signed in, when it is one rather than the
   * account holder: their role, whether they take new chats, and which chats
   * they see. `null` for the account holder and for staff.
   */
  member: SessionMember | null;
  permissions: string[];
  services: string[];
  navigation: NavGroup[];
  scope: { kind: string; department_id: string | null; team_id: string | null };
  scopes: ScopeOption[];
  client: { public_id: string; name: string } | null;
  /**
   * The partner a Partner Portal session belongs to, and the person's role
   * there (`owner`, `manager`, `staff`). `null` for staff and clients.
   */
  partner: { public_id: string; name: string; role: string } | null;
  /** A client's service selector entries, in order. Always empty for staff. */
  client_services: ClientService[];
  /**
   * Mandatory policies and consents this client has still to accept. Nonzero
   * keeps the client on the consent screen; the API refuses everything else
   * meanwhile. Always 0 for staff.
   */
  pending_consents: number;
  /**
   * Whether this client's messages actually leave. Off (a demo or review
   * account), the send screens say so up front and the API refuses each send.
   * Always true for staff.
   */
  sending_allowed: boolean;
}

export type SessionStatus = 'unknown' | 'loading' | 'authenticated' | 'anonymous';

/** What asking for a sign-in code says. The same whether or not the login exists. */
export interface CodeRequestResult {
  channel: 'sms' | 'email';
  /**
   * The route tried first. `whatsapp` may still become a text — at once, or when
   * WhatsApp reports the number is not on it — and asking again always texts.
   */
  route: 'whatsapp' | 'sms' | 'email';
  expires_in_seconds: number;
  resend_after_seconds: number;
  /** Development only: the server refuses to boot in production with this on. */
  debug_code?: string;
}

/**
 * A client's password sign-in that still needs its code (the API's
 * `client_auth.two_step`). No session exists yet; `handle` is worth nothing but
 * the chance to answer it, for a few minutes, once.
 */
export interface SecondStep {
  handle: string;
  expires_in_seconds: number;
  /** Whether the person's Connect app holds the key, and so shows a code. */
  app: boolean;
  /** The login email, masked, when a code can be emailed — else `null`. */
  email: string | null;
  /** The login mobile, masked, when a code can be texted — else `null`. */
  sms: string | null;
  resend_after_seconds: number;
}

/** What sending a second-step code says. */
export interface SecondStepCodeSent {
  via: 'email' | 'sms';
  destination: string;
  expires_in_seconds: number;
  resend_after_seconds: number;
  sends_left: number;
  /** Development only: the server refuses to boot in production with this on. */
  debug_code?: string;
}

/**
 * Sent with a password sign-in: this app can take a client's second step. A
 * caller that does not say so is refused after the right password rather than
 * given a session — see `_takes_two_step` on the API side.
 */
const TWO_STEP_HEADER = { 'X-Connect-Two-Step': '1' };

interface SessionState {
  status: SessionStatus;
  user: SessionUser | null;
  /**
   * What the sign-in page tells somebody whose session was ended rather than
   * left — "This device was signed out from another device" — in the server's
   * own words. `null` when there is nothing to explain.
   */
  signedOutNotice: string | null;
  /** A permission set, for O(1) checks in a render path. */
  permissions: Set<string>;
  services: Set<string>;

  /**
   * `login` is what the account signs in with: a username, an email address or
   * a sign-in mobile for staff, a login email or mobile number for a client.
   */
  signIn: (login: string, password: string) => Promise<SecondStep | null>;
  /** Send a code for a second step, the way the person picked. */
  sendSecondStepCode: (handle: string, via: 'email' | 'sms') => Promise<SecondStepCodeSent>;
  /** Answer a second step with a code; the session starts when it is right. */
  verifySecondStep: (handle: string, code: string) => Promise<void>;
  /**
   * Ask for a one-time sign-in code. The app's door decides whose: the admin
   * portal's codes are staff codes, the client app's are clients'.
   */
  requestCode: (login: string) => Promise<CodeRequestResult>;
  /** Sign in with the code that was sent. A code works once. */
  signInWithCode: (login: string, code: string) => Promise<void>;
  /** Load the session from the refresh cookie. Called once at boot. */
  restore: () => Promise<void>;
  refreshSession: () => Promise<void>;
  signOut: () => Promise<void>;
  switchScope: (option: ScopeOption) => Promise<void>;
  /** Forget the session; `notice` is what the sign-in page should say about it. */
  clear: (notice?: string | null) => void;
}

/**
 * A hint, in this browser, that somebody is signed in to this app.
 *
 * For the public website on the same host (erp-connect-web), which shows "Go to
 * app" instead of "Log in" when it is set. Only a label depends on it: the
 * session itself is the refresh cookie, which a page cannot read and this does
 * not replace — a stale marker shows "Go to app" and the app then asks for a
 * sign-in as it always would. Written when a session is established, removed
 * whenever one ends.
 */
export const SIGNED_IN_MARKER = 'signed-in';

function markSignedIn(on: boolean): void {
  if (on) saveJSON(SIGNED_IN_MARKER, true);
  else removeJSON(SIGNED_IN_MARKER);
}

function indexOf(user: SessionUser | null): { permissions: Set<string>; services: Set<string> } {
  return {
    permissions: new Set(user?.permissions ?? []),
    services: new Set(user?.services ?? []),
  };
}

export const useSession = create<SessionState>((set, get) => {
  /** Exchange credentials for a session: password or code, the rest is the same. */
  const startSession = async (
    path: string,
    body: Record<string, string>,
  ): Promise<SecondStep | null> => {
    set({ status: 'loading', signedOutNotice: null });
    try {
      const result = await api.post<{ access_token?: string; two_step?: SecondStep }>(path, body, {
        anonymous: true,
        headers: TWO_STEP_HEADER,
      });
      if (result.two_step) {
        // Not signed in yet, and not an error: the page asks for the code.
        set({ status: 'anonymous', user: null, permissions: new Set(), services: new Set() });
        return result.two_step;
      }
      setAccessToken(result.access_token ?? null);
      await get().refreshSession();
      return null;
    } catch (error) {
      markSignedIn(false);
      set({ status: 'anonymous', user: null, permissions: new Set(), services: new Set() });
      throw error;
    }
  };

  return {
  status: 'unknown',
  user: null,
  signedOutNotice: null,
  permissions: new Set<string>(),
  services: new Set<string>(),

  signIn: (login, password) => startSession('/auth/sign-in', { login, password }),

  requestCode: (login) =>
    api.post<CodeRequestResult>('/auth/code/request', { login }, { anonymous: true }),

  signInWithCode: async (login, code) => {
    await startSession('/auth/code/sign-in', { login, code });
  },

  sendSecondStepCode: (handle, via) =>
    api.post<SecondStepCodeSent>('/auth/two-step/send', { handle, via }, { anonymous: true }),

  verifySecondStep: async (handle, code) => {
    await startSession('/auth/two-step/verify', { handle, code });
  },

  restore: async () => {
    // A cold load has no access token in memory by design; the client sees
    // that and refreshes from the httpOnly cookie before asking who this is. A
    // refused refresh means nobody is signed in: the call fails without being
    // sent, and the person is shown the sign-in.
    set({ status: 'loading' });
    try {
      await get().refreshSession();
    } catch {
      markSignedIn(false);
      set({ status: 'anonymous', user: null, permissions: new Set(), services: new Set() });
    }
  },

  refreshSession: async () => {
    const fetched = await api.get<SessionUser>('/auth/me');
    // Defaulted at the one place the session enters: an API from before the
    // service selector omits the field, and every reader is written against an
    // array rather than each remembering that it may be missing.
    const user: SessionUser = {
      ...fetched,
      client_services: fetched.client_services ?? [],
      // Absent from an API older than client teams: the account holder.
      member: fetched.member ?? null,
      // Absent from an API older than the Partner Portal.
      partner: fetched.partner ?? null,
      pending_consents: fetched.pending_consents ?? 0,
      // Absent from an API older than the switch, where everything was sent.
      sending_allowed: fetched.sending_allowed ?? true,
    };
    if (user.kind !== servedKind()) {
      // The server already refuses the wrong kind at sign-in. This is the
      // second line: a token minted for the other app must not render a shell
      // here even if it somehow arrives.
      setAccessToken(null);
      markSignedIn(false);
      set({ status: 'anonymous', user: null, permissions: new Set(), services: new Set() });
      return;
    }
    setActiveScope(
      user.scope.kind === 'organization'
        ? { kind: 'organization', id: null }
        : { kind: user.scope.kind, id: user.scope.department_id ?? user.scope.team_id },
    );
    set({ status: 'authenticated', user, ...indexOf(user) });
    markSignedIn(true);
  },

  signOut: async () => {
    try {
      await api.post('/auth/sign-out', undefined, { anonymous: true });
    } catch {
      // Signing out locally must succeed even if the call did not. Leaving the
      // user apparently signed in because the network blipped is the worse
      // failure of the two.
    }
    get().clear();
  },

  switchScope: async (option) => {
    setActiveScope({
      kind: option.kind,
      id: option.department_public_id ?? option.team_public_id ?? null,
    });
    // Re-fetched rather than patched locally: the scope changes both the
    // permissions and the navigation, and the server is the only thing that
    // knows what it changes them to.
    await get().refreshSession();
  },

  clear: (notice = null) => {
    setAccessToken(null);
    setActiveScope(null);
    markSignedIn(false);
    set({ status: 'anonymous', user: null, permissions: new Set(), services: new Set(), signedOutNotice: notice });
  },
  };
});

/**
 * Wire the client's 401 handler to the store.
 *
 * Called once at boot. A hook rather than an import inside the client, because
 * the client must not depend on the store — see `setUnauthenticatedHandler`.
 */
export function installSessionHandlers(): void {
  setUnauthenticatedHandler((notice) => {
    useSession.getState().clear(notice);
  });
  setConsentRequiredHandler(() => {
    // A failure here is a session that has since ended, which the 401 handler
    // above already dealt with.
    useSession
      .getState()
      .refreshSession()
      .catch(() => undefined);
  });
}

// ── the UX-level checks ─────────────────────────────────────
/**
 * Whether the signed-in user holds a permission.
 *
 * Passing several means **any one is enough**: a page that gathers two
 * capabilities should appear as soon as either is granted, rather than
 * requiring both to show a screen that would work with one.
 */
export function useCan(permission: string | string[] | null | undefined): boolean {
  const permissions = useSession((state) => state.permissions);
  const superuser = useSession((state) => state.user?.is_superuser ?? false);
  if (permission === null || permission === undefined) return true;
  if (superuser) return true;
  const codes = Array.isArray(permission) ? permission : [permission];
  return codes.some((code) => permissions.has(code));
}

/**
 * Whether a service is enabled for this installation.
 *
 * **A superuser does not bypass this**, matching the server exactly. Enrollment
 * is a property of the installation rather than the person, so an owner offered
 * a screen the API would refuse is worse than an owner who is not offered it.
 */
export function useHasService(serviceId: string | null | undefined): boolean {
  const services = useSession((state) => state.services);
  if (!serviceId) return true;
  return services.has(serviceId);
}

/**
 * The navigation groups for the signed-in user, already filtered by the server.
 *
 * `readonly` because the signed-out result is a shared frozen array. A shell
 * that wants to reorder or filter this should map to its own array — mutating
 * what the store handed back would change it for every other reader.
 */
export function useNavigation(): readonly NavGroup[] {
  // `EMPTY`, not `[]`. Zustand 5 runs this selector inside `getSnapshot`, and a
  // fresh literal each call is a snapshot React sees as never settling.
  return useSession((state) => state.user?.navigation ?? EMPTY);
}

export function useCurrentUser(): SessionUser | null {
  return useSession((state) => state.user);
}

/** Non-reactive read, for an event handler or a query function. */
export function sessionSnapshot(): SessionUser | null {
  return useSession.getState().user;
}

export function can(permission: string | string[]): boolean {
  const state = useSession.getState();
  if (state.user?.is_superuser) return true;
  const codes = Array.isArray(permission) ? permission : [permission];
  return codes.some((code) => state.permissions.has(code));
}

export function hasService(serviceId: string): boolean {
  return useSession.getState().services.has(serviceId);
}
